"use client";

import { FormEvent, useEffect, useState } from "react";

type IdeaDraft = {
  lang: "de" | "en";
  title: string;
  problem: string;
  targetGroup: string;
  uniqueValue: string;
};

const STORAGE_KEY = "vtob_idea_draft";
const LANG_KEY = "vtob_lang";

const copy = {
  de: {
    title: "Idee erfassen",
    desc: "Erfasse die Kernpunkte deiner Geschaeftsidee als Grundlage fuer den ersten Businessplan-Entwurf.",
    lang: "Sprache",
    ideaTitle: "Titel der Idee",
    ideaTitlePh: "z.B. KI-gestuetzte Buchhaltungsassistenz",
    problem: "Welches Problem loest du?",
    problemPh: "Beschreibe den Schmerzpunkt deiner Zielgruppe.",
    target: "Zielgruppe",
    targetPh: "z.B. KMU in der Schweiz",
    unique: "Dein Alleinstellungsmerkmal (optional)",
    uniquePh: "Was macht deine Loesung einzigartig?",
    save: "Idee speichern",
    next: "Weiter zu Plan generieren",
    saved: "Entwurf gespeichert. Weiterleitung...",
  },
  en: {
    title: "Capture idea",
    desc: "Capture the key points of your business idea as the basis for a first business plan draft.",
    lang: "Language",
    ideaTitle: "Idea title",
    ideaTitlePh: "e.g. AI-powered accounting assistant",
    problem: "Which problem are you solving?",
    problemPh: "Describe the core pain point of your target audience.",
    target: "Target group",
    targetPh: "e.g. Healthcare SMEs in Switzerland",
    unique: "Unique value proposition (optional)",
    uniquePh: "What makes your solution unique?",
    save: "Save idea",
    next: "Continue to plan generation",
    saved: "Draft saved. Redirecting...",
  },
} as const;

export default function IdeeErfassenPage() {
  const [lang, setLang] = useState<"de" | "en">("de");
  const [form, setForm] = useState<IdeaDraft>({
    lang: "de",
    title: "",
    problem: "",
    targetGroup: "",
    uniqueValue: "",
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const current = window.localStorage.getItem(LANG_KEY) === "en" ? "en" : "de";
    setLang(current);
    setForm((prev) => ({ ...prev, lang: current }));

    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      const nextLang = detail === "en" ? "en" : "de";
      setLang(nextLang);
      setForm((prev) => ({ ...prev, lang: nextLang }));
    };
    window.addEventListener("vtob-lang-change", handler);
    return () => window.removeEventListener("vtob-lang-change", handler);
  }, []);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    setSaved(true);
    window.location.href = "/plan-generieren";
  }

  const t = copy[lang];

  return (
    <main className="vtob-container">
      <h1 style={{ marginBottom: 8 }}>{t.title}</h1>
      <p style={{ marginBottom: 24, color: "#555" }}>{t.desc}</p>

      <form className="vtob-card vtob-grid" onSubmit={onSubmit}>
        <label>
          {t.lang}
          <select
            value={form.lang}
            onChange={(e) => {
              const nextLang = e.target.value as "de" | "en";
              window.localStorage.setItem(LANG_KEY, nextLang);
              window.dispatchEvent(new CustomEvent("vtob-lang-change", { detail: nextLang }));
              setForm((prev) => ({ ...prev, lang: nextLang }));
              setLang(nextLang);
            }}
          >
            <option value="de">Deutsch</option>
            <option value="en">English</option>
          </select>
        </label>

        <label>
          {t.ideaTitle}
          <input
            name="title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            placeholder={t.ideaTitlePh}
            required
          />
        </label>

        <label>
          {t.problem}
          <textarea
            name="problem"
            value={form.problem}
            onChange={(e) => setForm((prev) => ({ ...prev, problem: e.target.value }))}
            rows={4}
            placeholder={t.problemPh}
            required
          />
        </label>

        <label>
          {t.target}
          <input
            name="targetGroup"
            value={form.targetGroup}
            onChange={(e) => setForm((prev) => ({ ...prev, targetGroup: e.target.value }))}
            placeholder={t.targetPh}
            required
          />
        </label>

        <label>
          {t.unique}
          <textarea
            name="uniqueValue"
            value={form.uniqueValue}
            onChange={(e) => setForm((prev) => ({ ...prev, uniqueValue: e.target.value }))}
            rows={3}
            placeholder={t.uniquePh}
          />
        </label>

        <div className="vtob-actions">
          <button className="vtob-btn" type="submit">
            {t.save}
          </button>
          <a className="vtob-link-btn" href="/plan-generieren">
            {t.next}
          </a>
        </div>
        {saved ? (
          <p style={{ color: "#0a7a2f", margin: 0 }}>
            {t.saved}
          </p>
        ) : null}
      </form>
    </main>
  );
}
